import { Loading } from '@nautilusbio/nb-design-system/Loading';
import clsx from 'clsx';
import React, {
  CSSProperties,
  HTMLElementType,
  RefObject,
  Suspense,
  createElement,
  forwardRef,
  useRef,
  ReactElement,
  ReactNode,
} from 'react';

import { useFirstViewportEntry } from '../../hooks';

import styles from './styles.module.css';

export type RenderOnViewportEntryProps = {
  as?: HTMLElementType;
  children: ReactNode | ReactElement;
  className?: string;
  fallback?: ReactNode;
  onClick?: () => void;
  onKeyDown?: () => void;
  threshold?: number;
  root?: Element | Document | null;
  rootMargin?: string;
  style?: CSSProperties;
};

const RenderOnViewportEntry = forwardRef<HTMLDivElement, RenderOnViewportEntryProps>(
  (
    {
      as = 'div',
      children,
      className,
      fallback = <Loading type="inline" />,
      threshold = 0,
      root = null,
      rootMargin = '0px 0px 0px 0px',
      ...wrapperProps
    },
    ref,
  ) => {
    const internalRef = useRef<HTMLDivElement | null>(null);

    // Properly merge refs (callback or RefObject)
    const setRef = (node: HTMLDivElement | null) => {
      internalRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref && typeof ref === 'object') {
        (ref as RefObject<HTMLDivElement | null>).current = node;
      }
    };

    const entered = useFirstViewportEntry(internalRef, {
      threshold,
      root,
      rootMargin,
    });

    return createElement(
      as,
      {
        ...wrapperProps,
        className: clsx(styles.renderOnEnter, className),
        ref: setRef,
      },
      entered && <Suspense fallback={fallback}>{children}</Suspense>,
    );
  },
);

RenderOnViewportEntry.displayName = 'RenderOnViewportEntry';

export default RenderOnViewportEntry;
